import * as React from 'react';
import { useState } from 'react';
import { FileOutlined, LinkOutlined, SoundOutlined } from '@ant-design/icons';
import { ContactAvatar } from '../contact_avatar/ContactAvatar';
import AudioNoteContent from '../audio_note_content/AudioNoteContent';
import { InternalOnlyChip } from '../internal_only_chip/InternalOnlyChip';
import { dateFormatterWithoutTimeZone } from '../../utils/DataFormatterUtils';
import {
	formatClipDuration,
	getNoteMediaAuthorName,
	NOTE_MEDIA_DATE_FORMAT,
	NOTE_MEDIA_TYPE_AUDIO,
	NOTE_MEDIA_TYPE_LINK,
	NoteMediaItem,
} from '../../utils/NoteMediaUtils';

const AUTHOR_AVATAR_SIZE = 24;

// Tailwind's preflight is off, so a border width only renders alongside an explicit border style.
// Each row draws its own top rule and the first one suppresses it, which keeps the divider on the
// element that owns it rather than on the list.
const ROW_CLASS_NAME =
	'border-0 border-t border-solid border-grey-4 first:border-t-0 hover:bg-grey-2';

// Doubles as the reset for the voice-note row, which is a button so it can be reached from the
// keyboard.
const ROW_MAIN_CLASS_NAME =
	'bodyregular flex w-full cursor-pointer flex-row items-center gap-3 border-0 bg-transparent px-4 py-3 text-left text-character-primary no-underline hover:text-character-primary';

const getRowIcon = (item: NoteMediaItem): React.ReactNode => {
	if (item.mediaType === NOTE_MEDIA_TYPE_AUDIO) {
		return <SoundOutlined />;
	}
	if (item.mediaType === NOTE_MEDIA_TYPE_LINK) {
		return <LinkOutlined />;
	}
	return <FileOutlined />;
};

interface NotesMediaRowMetaProps {
	item: NoteMediaItem;
}

// Who shared the item and when, pinned to the right of every row.
const NotesMediaRowMeta: React.FC<NotesMediaRowMetaProps> = ({ item }) => {
	// A log entry can name its author by email alone; the avatar still needs a contact to initial.
	const authorContact = item.addedByContact || { nameGiven: item.addedBy, nameFamily: '' };

	return (
		<div className="ml-4 flex shrink-0 flex-row items-center gap-2">
			<span className="text-character-secondary">--</span>
			<ContactAvatar contact={authorContact} size={AUTHOR_AVATAR_SIZE} hidePopover />
			<span className="bodyregular text-character-primary">{getNoteMediaAuthorName(item)}</span>
			<span className="text-character-secondary">•</span>
			<span className="bodyregular whitespace-nowrap text-character-secondary">
				{dateFormatterWithoutTimeZone(item.addedAt, NOTE_MEDIA_DATE_FORMAT)}
			</span>
		</div>
	);
};

interface NotesMediaRowProps {
	item: NoteMediaItem;
}

/**
 * One row of the Files / Links list: an attachment, a voice note or a link the conversation shared.
 * Files and links open in a new tab; a voice note expands in place into the same player and
 * transcription toggle it has in the chat.
 */
const NotesMediaRow: React.FC<NotesMediaRowProps> = ({ item }) => {
	const [durationSeconds, setDurationSeconds] = useState<number | null>(null);
	const [audioExpanded, setAudioExpanded] = useState(false);

	const isAudio = item.mediaType === NOTE_MEDIA_TYPE_AUDIO;

	const handleLoadedMetadata = (event: React.SyntheticEvent<HTMLAudioElement>) => {
		setDurationSeconds(event.currentTarget.duration);
	};

	const toggleAudioExpanded = () => setAudioExpanded(!audioExpanded);

	// A voice note's "subtitle" is its length, which only the loaded clip can tell us; a link's is
	// its host, which is known up front.
	const subtitle =
		isAudio && durationSeconds !== null ? formatClipDuration(durationSeconds) : item.subtitle;

	const rowContent = (
		<>
			<span className="flex shrink-0 text-[20px] leading-none text-character-secondary">
				{getRowIcon(item)}
			</span>
			<span className="flex min-w-0 flex-1 items-baseline gap-2">
				<span className="bodyregular truncate text-character-primary">{item.title}</span>
				{subtitle ? (
					<span className="bodyregular shrink-0 text-character-secondary">{subtitle}</span>
				) : null}
				{/* Same badge the notes timeline puts on the log entry that attached this file, so an
				    internal-only attachment is never mistaken for one the client can see. */}
				{item.isInternalOnly ? <InternalOnlyChip className="shrink-0" /> : null}
			</span>
			<NotesMediaRowMeta item={item} />
		</>
	);

	if (isAudio) {
		return (
			<div className={ROW_CLASS_NAME}>
				<button type="button" className={ROW_MAIN_CLASS_NAME} onClick={toggleAudioExpanded}>
					{rowContent}
				</button>
				{/* Off-screen, purely so the row can label the clip with its length before it is played. */}
				<audio
					className="hidden"
					src={item.url}
					preload="metadata"
					onLoadedMetadata={handleLoadedMetadata}
				/>
				{audioExpanded ? (
					// Indented to line up with the row's name.
					<div className="pb-3 pl-12 pr-4">
						<AudioNoteContent note={item.note} playerType="default" />
					</div>
				) : null}
			</div>
		);
	}

	return (
		<a
			className={`${ROW_CLASS_NAME} ${ROW_MAIN_CLASS_NAME}`}
			href={item.url}
			target="_blank"
			rel="noreferrer"
		>
			{rowContent}
		</a>
	);
};

export default NotesMediaRow;
